import type { CanvasPaneProps } from "@/features/canvas/components/canvas-pane"
import type { OpenCanvasPayload } from "@/features/canvas/stores/canvas-store"

import { CanvasSidebarPanel } from "@/features/canvas/components/canvas-sidebar-panel"
import { useCanvasStore } from "@/features/canvas/stores/canvas-store"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

const COMPACT_LAYOUT_BREAKPOINT = 1024

type OpenCanvasSidebarOptions = {
  paneProps?: Partial<CanvasPaneProps>
  title?: string
}

export function openCanvasInSidebar(
  payload: OpenCanvasPayload,
  options?: OpenCanvasSidebarOptions
): void {
  useContentSidebarStore.getState().openPanel({
    id: "canvas",
    title: options?.title ?? payload.title ?? "Canvas",
    headerMode: "none",
    content: <CanvasSidebarPanel paneProps={options?.paneProps} />,
    focusable: true,
    closeable: true,
    resizable: true,
    isDirty: () => useCanvasStore.getState().isDirty,
    confirmMessage: {
      title: "Unsaved Changes",
      description:
        "You have unsaved changes in the document editor. If you leave now, your changes will be lost.",
      confirmLabel: "Discard changes",
      cancelLabel: "Stay",
    },
    onBeforeOpen: () => {
      useCanvasStore.getState().openCanvas(payload)
    },
    onClose: () => {
      useCanvasStore.getState().clearCanvas()
    },
  })

  // Auto-focus canvas on compact viewports so it shows immediately (full-screen overlay),
  // preserving the expected mobile experience for all entry points. The "Chat with document"
  // flow overrides this by explicitly setting isFocused: false before navigating to chat.
  if (typeof window !== "undefined" && window.innerWidth < COMPACT_LAYOUT_BREAKPOINT) {
    const state = useContentSidebarStore.getState()
    if (!state.showConfirmDialog) {
      useContentSidebarStore.setState({ isFocused: true })
    }
  }
}

export function closeCanvasInSidebar(): void {
  const sidebarStore = useContentSidebarStore.getState()
  if (sidebarStore.currentPanel?.id !== "canvas") {
    return
  }
  sidebarStore.closePanel()
}
